import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Button, Stack, Typography } from '@mui/material';
import SelectBase from 'components/forms/fields/select-field/select-base/select-base.component';
import { useLocationHoursContext } from 'components/location-hours/location-hours.context';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const CustomHeaderToolbar = () => {
  const { t } = useTranslation();

  const {
    temporaryHours: {
      temporaryHoursRef,
      activeStart,
      activeEnd,
      handleCalendarDateChange,
      handleCalendarViewChange,
      calendarView,
    },
  } = useLocationHoursContext();

  const [isTodayDisabled, setIsTodayDisabled] = useState(true);
  const temporaryHoursCalendarApi = temporaryHoursRef.current?.getApi();

  const calendarViewTypes = [
    {
      label: t(
        'LocationHours.temporaryHours.calendar.customHeaderToolbar.calenderViews.list',
      ),
      id: 'list',
      test: 'TemporaryHoursCalendar.Toolbar.CalenderViews.List.Button',
    },
    {
      label: t(
        'LocationHours.temporaryHours.calendar.customHeaderToolbar.calenderViews.week',
      ),
      id: 'week',
      test: 'TemporaryHoursCalendar.Toolbar.CalenderViews.Week.Button',
    },
    {
      label: t(
        'LocationHours.temporaryHours.calendar.customHeaderToolbar.calenderViews.month',
      ),
      id: 'month',
      test: 'TemporaryHoursCalendar.Toolbar.CalenderViews.Month.Button',
    },
    {
      label: t(
        'LocationHours.temporaryHours.calendar.customHeaderToolbar.calenderViews.year',
      ),
      id: 'year',
      test: 'TemporaryHoursCalendar.Toolbar.CalenderViews.Year.Button',
    },
  ];

  useEffect(() => {
    const today = DateTime.local().startOf('day');

    const start = DateTime.fromJSDate(
      activeStart ?? temporaryHoursCalendarApi?.view.activeStart,
    );
    const end = DateTime.fromJSDate(
      activeEnd ?? temporaryHoursCalendarApi?.view.activeEnd,
    );

    setIsTodayDisabled(today >= start && today <= end);
  }, [activeEnd, activeStart, temporaryHoursCalendarApi]);

  return (
    <Stack
      direction="column"
      justifyContent="space-between"
      flexWrap="wrap"
      spacing={1}
    >
      <Typography variant="h2" margin={0}>
        {temporaryHoursCalendarApi?.view.title}
      </Typography>

      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          onClick={(): void =>
            handleCalendarDateChange(temporaryHoursCalendarApi, 'today')
          }
          data-testid="TemporaryHoursCalendar.Toolbar.Today.Button"
          size="small"
          disabled={isTodayDisabled}
        >
          {t('LocationHours.temporaryHours.calendar.customHeaderToolbar.today')}
        </Button>

        <Stack direction="row">
          <Button
            variant="outlined"
            onClick={(): void =>
              handleCalendarDateChange(temporaryHoursCalendarApi, 'prev')
            }
            sx={{ borderRadius: '4px 0 0 4px', minWidth: 30, px: 1.5 }}
            data-testid="TemporaryHoursCalendar.Toolbar.Previous.Button"
          >
            <ChevronLeftIcon />
          </Button>
          <Button
            variant="outlined"
            onClick={(): void =>
              handleCalendarDateChange(temporaryHoursCalendarApi, 'next')
            }
            sx={{ borderRadius: '0 4px 4px 0', minWidth: 30, px: 1.5 }}
            data-testid="TemporaryHoursCalendar.Toolbar.Next.Button"
          >
            <ChevronRightIcon />
          </Button>
        </Stack>

        <SelectBase
          options={calendarViewTypes}
          onChange={e =>
            handleCalendarViewChange(temporaryHoursCalendarApi, e.target.value)
          }
          value={calendarView}
          size="small"
          color="primary"
          fullWidth={false}
          data-testid="TemporaryHoursCalendar.Toolbar.View.Select"
        />
      </Stack>
    </Stack>
  );
};

export default CustomHeaderToolbar;
